/* eslint-disable boundaries/element-types */
import { FilterProps } from 'components/table/enhancements/filters/filterTypes'
import {
  removeFilter,
  useClickOutside,
} from 'components/table/enhancements/filters/filterUtils'
import { format, parseISO } from 'date-fns'
import {
  FilterContainer,
  FilterHeaderButton,
  LabelAndInputContainer,
  RelativeWrap,
  RemoveFilterButton,
  StyledCloseIcon,
  StyledInput,
  StyledLabel,
} from 'features/assetDatabase'
import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

const DateRangeContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
`

function formatDateString(dateString: string | undefined | null): string {
  if (!dateString) return ''

  try {
    const date = parseISO(dateString)
    if (isNaN(date.getTime())) {
      console.error('Invalid date:', dateString)
      return dateString
    }
    return format(date, 'MMM dd yyyy')
  } catch (error) {
    console.error('Error formatting date:', error)
    return dateString
  }
}

export const DateFilter = <TData,>({
  column,
  columnHeader,
  setActiveFilters,
  navigate,
  disableUrlFilters,
}: FilterProps<TData>) => {
  const filterContainerRef = useRef<HTMLDivElement>(null)
  const [isInputVisible, setInputVisible] = useState(true)
  const [currentFilterValue, setCurrentFilterValue] = useState<
    [string, string]
  >(['', ''])
  const columnFilterValue = column.getFilterValue() as [string, string]

  useEffect(() => {
    if (columnFilterValue && columnFilterValue !== currentFilterValue) {
      setCurrentFilterValue(columnFilterValue)
      setInputVisible(false)
    }
  }, [columnFilterValue, currentFilterValue])

  const handleInputChange =
    (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target
      const newValue = [...currentFilterValue] as [string, string]
      newValue[index] = value

      // Update the filter value with ISO string format
      const formattedValue = newValue.map((date) => {
        if (!date) return ''
        try {
          return new Date(date).toISOString()
        } catch (error) {
          console.error('Error formatting date:', error)
          return ''
        }
      }) as [string, string]

      column.setFilterValue(
        formattedValue[0] || formattedValue[1] ? formattedValue : undefined
      )
      setCurrentFilterValue(newValue)

      // Update URL parameters (only if URL filters are enabled)
      if (!disableUrlFilters) {
        if (newValue[0] || newValue[1]) {
          navigate({
            to: '.',
            search: (prev) => ({
              ...prev,
              [column.id]: formattedValue,
            }),
          })
        } else {
          removeFilter(column, setActiveFilters, navigate)
        }
      }
    }

  const handleOutsideClick = () => {
    if (!currentFilterValue[0] && !currentFilterValue[1]) {
      removeFilter(column, setActiveFilters, navigate)
    } else {
      setInputVisible(false)
    }
  }

  useClickOutside(filterContainerRef, handleOutsideClick)

  const getDisplayValue = () => {
    const [start, end] = currentFilterValue
    if (start && end) {
      return `${formatDateString(start)} - ${formatDateString(end)}`
    } else if (start) {
      return `After ${formatDateString(start)}`
    } else if (end) {
      return `Before ${formatDateString(end)}`
    }
    return ''
  }

  return (
    <RelativeWrap>
      <FilterHeaderButton
        onClick={() => setInputVisible((visible) => !visible)}>
        {columnHeader}
        {getDisplayValue() && (
          <RemoveFilterButton
            onClick={() => removeFilter(column, setActiveFilters, navigate)}>
            {getDisplayValue()}
            <StyledCloseIcon />
          </RemoveFilterButton>
        )}
      </FilterHeaderButton>
      {isInputVisible && (
        <FilterContainer
          ref={filterContainerRef}
          className={isInputVisible ? 'input-visible' : ''}>
          <DateRangeContainer>
            <LabelAndInputContainer>
              <StyledLabel>After:</StyledLabel>
              <StyledInput
                type='date'
                value={currentFilterValue[0]}
                onChange={handleInputChange(0)}
                placeholder='Start date'
              />
            </LabelAndInputContainer>
            <LabelAndInputContainer>
              <StyledLabel>Before:</StyledLabel>
              <StyledInput
                type='date'
                value={currentFilterValue[1]}
                onChange={handleInputChange(1)}
                placeholder='End date'
              />
            </LabelAndInputContainer>
          </DateRangeContainer>
        </FilterContainer>
      )}
    </RelativeWrap>
  )
}
